import {
  StudentSource,
  type StoreConversationInvitationRequest,
  type StoreConversationInvitationRequestLinksItem,
  type StoreConversationInvitationRequestQuestionsItem,
  type UpdateConversationInvitationRequest,
  type UpdateConversationInvitationRequestLinksItem,
} from "api/generated";
import type {
  ConversationInvitationFormData,
  ConversationMediaFormData,
  ConversationQuestionFormData,
} from "conversations/types";
import { toSchoolClock } from "shared/utils";

const mediaToStoreDto = (
  media: ConversationMediaFormData,
): StoreConversationInvitationRequestLinksItem => ({
  name: media.name,
  url: media.url,
});

const mediaToUpdateDto = (
  media: ConversationMediaFormData,
): UpdateConversationInvitationRequestLinksItem =>
  media.id === null
    ? { name: media.name, url: media.url }
    : { id: media.id, name: media.name, url: media.url };

/**
 * Options only travel with a radio question; the backend rejects them on any other type
 * rather than ignoring them, so the key is dropped instead of sent empty.
 */
const questionToDto = (
  question: ConversationQuestionFormData,
): StoreConversationInvitationRequestQuestionsItem => {
  const base = {
    question: question.question,
    type: question.type,
    required: question.required,
    order: question.order,
  };

  return question.type === "radio"
    ? { ...base, options: question.options }
    : base;
};

/**
 * Exactly one criteria array is sent — the mode named by `students_selected_by`. The
 * other three are omitted, not sent empty: the backend `prohibited`s them, so including
 * them is a 422.
 */
const criteriaFields = (formData: ConversationInvitationFormData) => {
  switch (formData.studentsSelectedBy) {
    case StudentSource.education_levels:
      return { education_levels: formData.educationLevels };
    case StudentSource.mentor_classes:
      return { mentor_classes: formData.mentorClasses };
    case StudentSource.lesson_groups:
      return { lesson_groups: formData.lessonGroups };
    case StudentSource.student_ids:
      return { student_ids: formData.studentIds };
    default:
      return {};
  }
};

/**
 * `date` is sent as the Dutch wall-clock reading of the instant, matching what the
 * backend stores — see `conversationDto.mapper`.
 */
const sharedFields = (formData: ConversationInvitationFormData) => ({
  conversation_type_id: Number(formData.conversationTypeId),
  name: formData.name,
  description: formData.description === "" ? null : formData.description,
  date: formData.date === null ? "" : toSchoolClock(formData.date),
  // Null, never "": the key is optional and a blank string would fail the date rule.
  end_date: formData.endDate === null ? null : toSchoolClock(formData.endDate),
  // Same as the conversation payload: an unchosen vak is null, never "".
  subject_shortage:
    formData.subjectShortage === "" ? null : formData.subjectShortage,
  students_selected_by: formData.studentsSelectedBy as StudentSource,
  // Both always sent: the columns carry no default on either side, so the form is what
  // decides (CONVERSATION-INVITATION-ATTACHMENT-FLAGS-001).
  attachment_allowed: formData.attachmentAllowed,
  // Never true while bijlagen are off — the backend refuses that pair, and the form's
  // toggle is disabled in that state.
  attachment_required:
    formData.attachmentAllowed === true ? formData.attachmentRequired : false,
  ...criteriaFields(formData),
});

export const conversationInvitationFormDataToStoreDto = (
  formData: ConversationInvitationFormData,
): StoreConversationInvitationRequest => ({
  ...sharedFields(formData),
  questions: formData.questions.map(questionToDto),
  links: formData.links.map(mediaToStoreDto),
  videos: formData.videos.map(mediaToStoreDto),
});

/**
 * No `questions`: the set is frozen once the invitation exists, and the backend rejects
 * the key rather than ignoring it.
 */
export const conversationInvitationFormDataToUpdateDto = (
  formData: ConversationInvitationFormData,
): UpdateConversationInvitationRequest => ({
  ...sharedFields(formData),
  links: formData.links.map(mediaToUpdateDto),
  videos: formData.videos.map(mediaToUpdateDto),
});
